import { type BoundActions, createHook, type HookFunction } from 'react-sweet-state';

import {
	ActionStepApplyStore,
	type ActionStepApplyActions,
	type ActionStepApplyEntry,
	type ActionStepApplyStoreState,
} from './action-step-apply-store-internal';

/**
 * Reads the apply entries for a given `conversationId` + list of `invocationId`s in a single
 * selector call, defaulting missing entries to `{ inProgress: false, error: undefined }`.
 *
 * Shared by both `useActionStepApplyState` (single step) and `useActionPlanApplyState`
 * (aggregate) so neither needs to call the other in a loop, which would break the rules of hooks.
 */
export const useActionStepApplyEntriesHook: HookFunction<
	Record<string, ActionStepApplyEntry>,
	BoundActions<ActionStepApplyStoreState, ActionStepApplyActions>,
	{ conversationId: string; invocationIds: string[] }
> = createHook(ActionStepApplyStore, {
	selector: (
		state: ActionStepApplyStoreState,
		{ conversationId, invocationIds }: { conversationId: string; invocationIds: string[] },
	): Record<string, ActionStepApplyEntry> => {
		const entries: Record<string, ActionStepApplyEntry> = {};
		for (const invocationId of invocationIds) {
			entries[invocationId] = state.byConversation[conversationId]?.[invocationId] ?? {
				inProgress: false,
				error: undefined,
			};
		}
		return entries;
	},
});
